import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, 
  Image, 
  Video, 
  Zap, 
  Clock, 
  Star,
  Info,
  CheckCircle,
  ExternalLink,
  Sparkles,
  Filter,
  Search,
  FileImage,
  Palette,
  Settings
} from 'lucide-react';
import { cn } from '../utils';
import { useModels } from '../hooks/useModels';

export default function ModelSelector({ selectedModel, onModelSelect, category = 'all' }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(category);
  const [showOnlyFree, setShowOnlyFree] = useState(false);
  const [showOnlyPopular, setShowOnlyPopular] = useState(false);
  
  const { models, categories, getModelsByCategory, getFreeModels, getPopularModels } = useModels();
  
  // Filtra i modelli in base ai criteri selezionati
  const getFilteredModels = () => {
    let filteredModels = models;
    
    // Filtra per categoria
    if (selectedCategory !== 'all') {
      filteredModels = getModelsByCategory(selectedCategory);
    }
    
    // Filtra per gratuiti
    if (showOnlyFree) {
      filteredModels = filteredModels.filter(model => model.free);
    }
    
    // Filtra per popolari
    if (showOnlyPopular) {
      filteredModels = filteredModels.filter(model => model.popular);
    }
    
    // Filtra per termine di ricerca
    if (searchTerm) {
      filteredModels = filteredModels.filter(model => 
        model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        model.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        model.features.some(feature => feature.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    return filteredModels;
  };
  
  const filteredModels = getFilteredModels();
  
  // Mappa delle icone
  const iconMap = {
    'Zap': Zap,
    'Star': Star,
    'Sparkles': Sparkles,
    'FileImage': FileImage,
    'Palette': Palette,
    'Settings': Settings,
    'Video': Video,
    'Brain': Brain,
    'Image': Image
  };
  
  const renderModelCard = (model) => {
    const IconComponent = iconMap[model.icon] || Settings;
    const isSelected = selectedModel?.id === model.id;
    
    return (
      <motion.div
        key={model.id}
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={cn(
          "relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200",
          isSelected
            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
            : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
          "bg-white dark:bg-gray-800 shadow-sm hover:shadow-md"
        )}
        onClick={() => onModelSelect(model)}
      >
        {/* Badge per modelli popolari */}
        {model.popular && (
          <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium">
            Popolare
          </div>
        )}
        
        {/* Header con icona e nome */}
        <div className="flex items-start gap-3 mb-3">
          <div className={`p-2 rounded-lg bg-gradient-to-r ${model.color}`}>
            <IconComponent className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
              {model.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {model.subcategory}
            </p>
          </div>
        </div>
        
        {/* Descrizione */}
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
          {model.description}
        </p>
        
        {/* Features */}
        <div className="flex flex-wrap gap-1 mb-3">
          {model.features.slice(0, 3).map((feature, index) => (
            <span
              key={index}
              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full"
            >
              {feature}
            </span>
          ))}
        </div>
        
        {/* Info footer */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3" />
            <span>{model.avgTime}</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
            <span>{model.rating}</span>
          </div>
          <div className={cn(
            "px-2 py-1 rounded-full font-medium",
            model.free
              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
          )}>
            {model.free ? 'Gratuito' : `$${model.cost}`}
          </div>
        </div>
        
        {/* Indicatore selezione */}
        {isSelected && (
          <div className="absolute top-2 right-2">
            <CheckCircle className="w-5 h-5 text-blue-500" />
          </div>
        )}
      </motion.div>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Header e filtri */}
      <div className="space-y-4">
        {/* Barra di ricerca */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Cerca modelli..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        
        {/* Filtri categoria */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={cn(
              "px-3 py-1 rounded-full text-sm font-medium transition-colors",
              selectedCategory === 'all'
                ? "bg-blue-500 text-white"
                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            )}
          >
            Tutti
          </button>
          {Object.entries(categories).map(([key, category]) => {
            const IconComponent = iconMap[category.icon] || Brain;
            return (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={cn(
                  "flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-colors",
                  selectedCategory === key
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                )}
              >
                <IconComponent className="w-3 h-3" />
                {category.name}
              </button>
            );
          })}
        </div>
        
        {/* Filtri aggiuntivi */}
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showOnlyFree}
              onChange={(e) => setShowOnlyFree(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-gray-600 dark:text-gray-300">Solo gratuiti</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showOnlyPopular}
              onChange={(e) => setShowOnlyPopular(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-gray-600 dark:text-gray-300">Solo popolari</span>
          </label>
        </div>
      </div>
      
      {/* Griglia modelli */}
      <motion.div 
        layout
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        <AnimatePresence>
          {filteredModels.map(renderModelCard)}
        </AnimatePresence>
      </motion.div>
      
      {/* Messaggio nessun risultato */}
      {filteredModels.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-2">
            <Search className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            Nessun modello trovato
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Prova a modificare i filtri di ricerca
          </p>
        </div>
      )}
    </div>
  );
}